import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import jsonpath from "fast-json-patch"

import IconDistribute from "assets/img/icon-distribute.svg?react"
import IconPencil from "assets/img/icon-pencil.svg?react"

import Modal from "components/Modal/Modal"
import InvoiceDetails from "models/InvoiceDetails"
import FinanceService from "services/FinanceService"
import Button from "components/Button/Button"
import Tabs from "components/Tabs/Tabs"
import TabsItem from "components/Tabs/TabsItem/TabsItem"
import Routes from "@/routes"
import InvoiceSidebarSummary from "./InvoiceSidebarSummary"
import InvoiceSidebarAssets from "./InvoiceSidebarAssets"
import { IApiLink, Guid } from "@/types"
import toastMessages from "stores/toastMessages"
import Invoice, { InvoiceAssignmentStatus, IPostInvoiceDto } from "models/Invoice"
import InvoiceModal from "components/InvoiceModal/InvoiceModal"
import { Flex } from "components/Flex"
import financeSettingsStore from "stores/financeSettings"
import { usePermissions } from "stores/permissions"
import { UserPermission } from "config/constants"

export interface IInvoiceSidebarProps {
    invoiceId: Guid
    getInvoiceDetailsLink: IApiLink
    onClose?(): void
}

const InvoiceSidebar: FunctionComponent<React.PropsWithChildren<IInvoiceSidebarProps>> = ({ onClose, getInvoiceDetailsLink, invoiceId }) => {
    const { t } = useTranslation()
    const {hasPermission} = usePermissions();
    const location = useLocation()
    const scrollEl = useRef<HTMLDivElement>(null)
    const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetails | null>(null)
    const [isAssignmentCompleted, setIsAssignmentCompleted] = useState<boolean>(false)
    const [hasAssetsTabBeenActivated, setHasAssetsTabBeenActivated] = useState<boolean>(false)
    const [updatingInvoice, setUpdatingInvoice] = useState<Invoice | null>(null)

    const updatePermission = hasPermission(UserPermission.IncomingInvoicesEdit)

    const fetchInvoiceDetails = useCallback(async () => {
        try {
            const newInvoiceDetails = await FinanceService.getInvoiceDetails(getInvoiceDetailsLink)
            setInvoiceDetails(newInvoiceDetails)
            setIsAssignmentCompleted(newInvoiceDetails.basicData.assignmentStatus !== InvoiceAssignmentStatus.notAssigned)
        } catch (e) {
            toastMessages.showSystemError()
            if (typeof onClose === "function") {
                onClose()
            }
        }
    }, [getInvoiceDetailsLink, onClose])

    useEffect(() => {
        fetchInvoiceDetails()
    }, [fetchInvoiceDetails])

    const handleTabClick = (index: number) => {
        if (index === 1 && !hasAssetsTabBeenActivated) {
            setHasAssetsTabBeenActivated(true)
        }
    }

    const handleUpdateInvoiceClick = async () => {
        try {
            const invoice = await FinanceService.getInvoice(invoiceId)
            const costCategories = await financeSettingsStore.fetchCostCategories()
            if (!costCategories?.list.length) throw Error()
            setUpdatingInvoice(invoice)
        } catch (e) {
            toastMessages.showSystemError()
        }
    }

    async function handleInvoiceUpdate(updatedInvoiceDto: IPostInvoiceDto, originalInvoice: Invoice) {
        if (!originalInvoice.updateLink) return null
        try {
            const operations = jsonpath.compare(originalInvoice.toPostDto(), updatedInvoiceDto)
            const res = await FinanceService.updateInvoice(originalInvoice.updateLink, operations)
            fetchInvoiceDetails()
            return res
        } catch (e) {
            return null
        }
    }

    return (
        <>
            <Modal type="sidebar"
                title={t('invoices.invoiceDetails')}
                onClose={onClose}
                isBusy={!invoiceDetails}
                modalBodyContentEl={scrollEl}
                maxWidth="608px"
                closeButton={!(invoiceDetails && !isAssignmentCompleted && updatePermission)}
                toolbar={
                    (invoiceDetails && !isAssignmentCompleted && updatePermission) ?
                        <Flex>
                            <Button
                                round
                                theme="grey"
                                icon={<IconPencil />}
                                className={"mr-2"}
                                aria-label={t('invoices.assignAmount')}
                                onClick={() => { handleUpdateInvoiceClick() }}
                            />
                            <Button
                                type="link"
                                round
                                theme="grey"
                                url={Routes.invoiceAssignAmount.getPath({ invoiceId }, { backUrl: location.pathname })}
                                icon={<IconDistribute />}
                                aria-label={t('invoices.assignAmount')} />
                        </Flex>
                        : undefined
                }>
                {invoiceDetails &&
                    <Tabs onSwitchTab={handleTabClick} data-testid="tabs">
                        <TabsItem label={t('invoices.overview')}>
                            <InvoiceSidebarSummary summary={invoiceDetails?.getSummaryBasicData()} />
                        </TabsItem>
                        <TabsItem label={t('invoices.assets')}>
                            {hasAssetsTabBeenActivated &&
                                <InvoiceSidebarAssets invoiceId={invoiceId} assets={invoiceDetails.assets} isAssignmentCompleted={isAssignmentCompleted} />
                            }
                        </TabsItem>
                    </Tabs>
                }
            </Modal>
            {!!updatingInvoice &&
                <InvoiceModal
                    originalInvoice={updatingInvoice}
                    onClose={() => setUpdatingInvoice(null)}
                    onSubmit={handleInvoiceUpdate}
                />
            }
        </>
    )
}

export default InvoiceSidebar
